import React, { FunctionComponent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next'

import {
  Divider,
  Grid,
  Paper,
} from '@mui/material';

import {
  Button,
  Dt,
  Typography
} from '../../../../components/v2/styled';

import { loginAtom } from '../../../../lib/auth';
import { User } from '../../../../models/User';
import HelpersClientWidgets from '../../../../actions/helpers/client_widgets';

// Per-field render config. gender + birth_date need a render node (translated
// label / date formatter) rather than a plain string value, so FieldRender
// supports an optional `node` + `isEmpty` pair alongside the plain `value`.
// Kept here (UI concern), same split as DetailsContact's FIELD_RENDER. The
// whole widget is gated on User::User by BaseMapper, so every field's gate is
// a no-op here (kept for shape parity with the other details widgets).
type FieldRender = {
  labelKey: string;
  gate: (object: User) => boolean;
  value?: (object: User) => string;
  node?: (object: User, t: any) => React.ReactNode;
  isEmpty?: (object: User) => boolean;
};

const FIELD_RENDER: Record<string, FieldRender> = {
  gender: {
    labelKey: 'coach.models.clients.gender',
    gate: () => true,
    isEmpty: (o) => o.gender !== 0 && o.gender !== 1 && o.gender !== 2,
    node: (o, t) => {
      if (o.gender === 0) return t('general.genders.female');
      if (o.gender === 1) return t('general.genders.male');
      if (o.gender === 2) return t('general.genders.other');
      return null;
    },
  },
  spouse_name: {
    labelKey: 'coach.models.clients.spouse_name',
    gate: () => true,
    value: (o) => o.spouse_name || '',
  },
  birth_date: {
    labelKey: 'coach.models.clients.birth_date',
    gate: () => true,
    isEmpty: (o) => !o.birth_date,
    node: (o) => <Dt o={o.birth_date} f="dd/LL/y" />,
  },
  national_number: {
    labelKey: 'coach.models.clients.national_number',
    gate: () => true,
    value: (o) => o.national_number || '',
  },
};

type Props = {
  object: User;
}

export const CoachUserWidgetsDetailsExtra: FunctionComponent<Props> = ({
  object
}) => {
  const [login] = useAtom(loginAtom);
  const navigate = useNavigate();
  const {t} = useTranslation(['translations']);

  // Extra settings are a coach preference, stored alongside the widget layout
  // on login.settings.userdetails_widgets.settings. `details_extra` bundles
  // { fields, hide_empty, edit } under one composite value.
  const widgetSettings = login?.settings?.userdetail_widgets?.settings;
  const cfg = widgetSettings?.[HelpersClientWidgets.detailsExtraValueKey] || {};
  const fields = cfg.fields || {};
  const hideEmpty = cfg.hide_empty !== false; // default on
  const editOn = cfg.edit !== false;          // default on

  // Fixed-order field items, each tagged with shown/hidden state.
  const ordered = HelpersClientWidgets.getOrderedDetailsExtraFields(fields);

  // Render only shown fields whose gate passes; when `hide_empty` is on, also
  // skip fields flagged empty. Node-based fields use their own isEmpty; plain
  // fields are empty when their string value is blank.
  const rows = ordered
    .filter((item) => item.state === 'shown')
    .map((item) => {
      const render = FIELD_RENDER[item.key];
      if (!render || !render.gate(object)) return null;
      let content: React.ReactNode;
      let empty: boolean;
      if (render.node) {
        empty = render.isEmpty ? render.isEmpty(object) : false;
        content = empty ? null : render.node(object, t);
      } else {
        const v = render.value ? render.value(object) : '';
        empty = !v;
        content = v;
      }
      if (hideEmpty && empty) return null;
      return { key: item.key, labelKey: render.labelKey, content };
    })
    .filter(Boolean) as { key: string; labelKey: string; content: React.ReactNode }[];

  // Edit affordance gate: only real users with basic consent. Toggled by the
  // widget's `edit` setting (default on).
  const canEdit = editOn
    && object?.type === 'User::User'
    && !!object.consent_status?.is_basic;

  return <Paper sx={{padding: 2, marginTop: 1, position: 'relative'}}>
    {/* Edit CTA: small text button top-right, absolute in the title row
        (space-saving — costs no extra vertical space). Same navigate target
        as before. */}
    {canEdit && <Button
      label={t("coach.general.actions.edit_extra")}
      id="edit_extra"
      text
      size="small"
      startIcon={<HelpersClientWidgets.detailsExtraEditIcon sx={{fontSize: '1.1rem'}} />}
      sx={{position: 'absolute', top: 4, right: 4, marginTop: 0, zIndex: 2}}
      onClick={() => {
        navigate(`/user/${object.id}`, {state: {detail: true}});
      }}
    />}

    <Grid container spacing={1}>
      <Grid item xs={12}>
        <Typography variant="h5" html>{t("coach.submenu.detail.other")}</Typography>
        <Divider />
      </Grid>
      {rows.map((row) => <Grid item xs={12} key={row.key}>
        <Typography variant="subtitle2" html>{t(row.labelKey)}</Typography>
        <Typography>{row.content}</Typography>
      </Grid>)}
    </Grid>
  </Paper>;
}
